import { AppBar, Box, Button, Grid, Toolbar, useTheme } from "@mui/material";
import { AuthService, ClientService } from "../../services";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedSnackbar,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { IEmployee, IGetListRequest } from "../../models";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { checkSINValidity, formatDate, getProvinceDDLLabelByShortCode, useNavigateToLogout, useRemoveQueriesOnUnmount } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { AgDataGridReact } from "../AgDataGridReact";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import { EmployeeService } from "../../services/EmployeeService";
import { IAppState } from "../../store/reducers";
import { PrimeDataGrid } from "../PrimeDataGrid";
import { PrimeDataGridWithColumnToggled } from "../PrimeDataGridWithColumnToggled";
import { SkeletonLoadingDataGrid } from "../SkeletonLoadingDataGrid";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

// import { CountryService } from "../../services/CountryService";

export interface IEmployeeList { }
const EMPLOYEE_LIST_QUERY_KEY = "employee-list";

const useEmployeees = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [EMPLOYEE_LIST_QUERY_KEY, clientId, params],
		queryFn: () => EmployeeService.getEmployee(clientId, params, navigateToLogout),
		// refetchOnWindowFocus: false,

		// ####################################FOR SLOW PAGGING ISSUE 100 #####################################
		staleTime: 0,              // always stale
		gcTime: 0,                 // no cache persistence
		refetchOnMount: "always",  // always refetch
		refetchOnWindowFocus: false,
	});
// const useGetCountryDDL = () =>
// 	useQuery({
// 		queryKey: ["country-ddl"],
// 		queryFn: (data) => {
// 			return CountryService.getCountryDDL();
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const hasObjectChanged = (
	obj1: Record<string, any>,
	obj2: Record<string, any>
): boolean => {

	const keys: any = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
	for (const key of keys) {
		if (obj1[key] !== obj2[key]) {
			return true;
		}
	}
	return false;
};
const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record";
const useGetClientInformationById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return ClientService.getSimpleClientById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetMaskStatus = () =>
	useQuery({
		queryKey: ["mask-status"],
		queryFn: (data) => {
			return AuthService.getMaskStatus();
		},
		refetchOnWindowFocus: false,
	});

export const EmployeeList: React.FC<IEmployeeList> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const canadaProvinces = useSelector((state: IAppState) => state.countryAndProvincesInfo.canadaProvinces);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const countryList = useSelector((state: IAppState) => state.countryAndProvincesInfo.countryDDL);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [employeeListTableData, setEmployeeListTableData] = useState([]);

	// const companyInformation = useSelector((state: IAppState) => state.dashboardApisData.client_detail)
	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { id } = useSelector((state: IAppState) => state.accountant);

	const { isPending: isPendingMaskStatus, data: maskStatus } =
		useGetMaskStatus();

	const { isPending: isPendingClientInformationById, data: companyInformation } = useGetClientInformationById(
		clientId,
		navigateToLogout
	);
	const { isPending, data } = useEmployeees(
		clientIdStore,
		queryParams,
		navigateToLogout
	); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const employeeList: any | null = data;

	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
		useGetPayFrequencyDDL(clientId, taxYear);

	// const { isPending: isPendingCountryDDL, data: countryList } =
	// 	useGetCountryDDL();

	const { message, componentState } = useSelector(
		(state: IAppState) => state.viewState
	);

	// const { accountantList } = useSelector(
	//   (state: IAppState) => state.accountant
	// );

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [selectedEmployeeListItems, setSelectedEmployeeListItems] = useState(
		[]
	);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroEmployee, setzeroEmployee] = useState(true);
	const [employeeListUpdatedItems, setEmployeeListUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [employeeId, setEmployeeId] = useState<any>(0);

	// Bulk Delete
	const [bulkDelete, setBulkDelete] = useState<boolean>(false);
	const [bulkEmployeeId, setBulkEmployeeId] = useState<any>({});
	const [bulkEmployeeDelete, setBulkEmployeeDelete] = useState<boolean>(false);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const [provinceOfEmployementDDL, setprovinceOfEmployementDDL] =
		useState<any>(undefined);
	useEffect(() => {
		if (countryList && Array.isArray(countryList)) {
			const canada = countryList.find(
				(item: any) => item.label === "Canada" || "canada"
			);
			if (canada) {
				(async () => {
					try {
						const response = await CountryService.getProvinceOrStateDDL(
							navigateToLogout,
							canada.value,
							"Province"
						);
						setprovinceOfEmployementDDL(response);
					} catch (err) {
						console.error("error", err);
					}
				})();
			} else {
				setprovinceOfEmployementDDL(undefined);
			}
		}
	}, [countryList]);


	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: EmployeeService.submitAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {

			if (res.status === 200) {
				// setSnackbarData({
				// 	open: true,
				// 	message: res.data.message ? res.data.message : "Something went wrong.",
				// 	severity: "success",
				// });

				return queryClient.invalidateQueries({
					queryKey: [EMPLOYEE_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	const {
		isPending: isSubmittingUpdatedRows,
		mutate: submitUpdatedRows,
	} = useMutation({
		mutationFn: EmployeeService.submitUpdatedRows,
		onSuccess: (res: any | IServerError | IResponse) => {

			if (res.status === 200) {
				// setSnackbarData({
				// 	open: true,
				// 	// message: res.data.message ? res.data.message : "Something went wrong.",
				// 	message: "Success",
				// 	severity: "success",
				// });

				if (res.data.message) {
					setSnackbarData({
						open: true,
						message: res.data.message,
						severity: res.data.message.startsWith("0") ? "warning" : "success",
					});
				}

				return queryClient.invalidateQueries({
					queryKey: [EMPLOYEE_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});

	useEffect(() => {

		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	const { isPending: isSubmittingColumnReorder, mutate: submitColumnReorder } =
		useMutation({
			mutationFn: EmployeeService.submitColumnReorder,
			onSuccess: (res: any | IServerError | IResponse) => {

				if (res.status === 201) {
					// setSnackbarData({
					// 	open: true,
					// 	message: res.data.message ? res.data.message : "Something went wrong.",
					// 	severity: "success",
					// });

					return queryClient.invalidateQueries({
						queryKey: [EMPLOYEE_LIST_QUERY_KEY],
					});
				} else {
					setSnackbarData({
						open: true,
						message: res.message
							? res.message
							: "Something went wrong, try again later!",
						severity: "error",
					});
				}
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	useEffect(() => {
		if (onColumnReorder) {
			submitColumnReorder({
				headerColumns: onColumnReorder,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [onColumnReorder]);

	// const onSearchButtonClicked = () => {
	// 	setQueryParams(searchText);
	// 	setzeroEmployee(false);
	// };

	const onRowEditClicked = useCallback((params: IEmployee) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/employee/setup/${params.id}`;
		// let path = `/employee/setup/${params.employeeId}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	// const callAction = () => {
	// 	if (apiAction === "delete") {
	// 		alert(`Delete row with ID: ${selectedIds} has been clicked!`);
	// 		// TODO api call for delete
	// 	} else if (apiAction === "updateStatus") {
	// 		alert(`action need to trigger : ${apiAction}`);
	// 		// TODO api call for update status
	// 	}
	// 	setDialogOpen(false);
	// };

	// For Delete Row
	const deleteEmployeeField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				employeeId: employeeId,
				navigateToLogout: navigateToLogout,
			};
			deleteEmployeeById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: IEmployee) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setApiAction("delete");
		setEmployeeId(params.id);
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteEmployeeById } = useMutation({
		mutationFn: EmployeeService.delEmployee,

		onSuccess: (res: string | IServerError | IResponse | any) => {

			if (typeof (res) === 'string') {
				// remove the cache of the Year fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: res,
					severity: "success",
				});

				return queryClient.invalidateQueries({
					queryKey: [EMPLOYEE_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : res.success === false ? "error" : "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setStatusValue(event.target.value as string);
		setApiAction("updateStatus");
		setDialogOpen(true);
	};

	// const onKeyFieldClickHandler = (clickedRow: any) => {
	// dispatch(setSelectedEmployee(clickedRow));
	// dispatch(setSelectedEmployeeName(clickedRow));
	// };

	//bulk delete

	const { isPending: isBulkDeleting, mutate: deleteBulkEmployeees } =
		useMutation({
			mutationFn: EmployeeService.delBulkEmployeees,
			onSuccess: (res: any | string | IServerError | IResponse) => {
				if (typeof res === "object") {
					// remove the cache of the Year fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong.",
						severity: res.status === false ? "error" : "success",
					});

					setBulkEmployeeDelete(false);
					return queryClient.invalidateQueries({
						queryKey: [EMPLOYEE_LIST_QUERY_KEY],
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				setBulkEmployeeDelete(false);
			},
			onError: (err: any) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
				setBulkEmployeeDelete(false);
			},
		});

	// For Bulk Delete
	const deleteBulkEmployeeField = () => {
		if (bulkDelete) {
			setBulkDelete(false);
			let payload = {
				employeeIds: bulkEmployeeId,
				navigateToLogout: navigateToLogout,
			};
			deleteBulkEmployeees(payload);
			setDialogOpen(false);
		}
	};

	const onRowMultipleDeleteClicked = (params: any) => {

		let bulkDeletePayload = {
			employeeIds: { employeeIds: params },
		};
		setDialogOpen(true);
		setBulkDelete(true);
		setApiAction("delete");
		setBulkEmployeeId(bulkDeletePayload);
		setBulkEmployeeDelete(true);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		// setSelectedIds(selectedItems);
		setSelectedEmployeeListItems(selectedItems);

		if (selectedItems.length) {
			return (
				// <Box sx={{ marginTop: theme.spacing(1) }}>
				// 	<Button
				// 		onClick={() => onRowMultipleDeleteClicked(selectedItems)}
				// 		variant="contained"
				// 		color="error"
				// 	>
				// 		Delete
				// 	</Button>
				// </Box>
				<></>
			);
		}
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton to="/employee/setup" text="Add Employee" />
			// </Box>
			<></>
		);
	}, []);
	// useEffect(() => {
	// 	return () => {
	// 		queryClient.removeQueries({ queryKey: ['employee-list'] });
	// 		// queryClient.removeQueries({ queryKey: ['country-ddl'] });
	// 	};
	// }, []);
	useRemoveQueriesOnUnmount(["employee-list"]);
	// useRemoveQueriesOnUnmount(["country-ddl", "province-ddl", "city-ddl"]);
	const upadtedEmployeeListItems = useMemo(() => {
		return (
			employeeList &&
			employeeList.items &&
			employeeList?.items.map((item: any) => ({
				...item,
				// empTitle: (
				//  <Link
				//      to={`/employee/setup/${item.id}`}
				//      className="no-underline"
				//      style={{ color: theme.palette.primary.light }}
				//  >
				//      {item.empTitle}
				//  </Link>
				// ),
				// provinceOfEmployment: item.provinceOfEmployment && item.provinceOfEmployment.id ? item.provinceOfEmployment.id : item.provinceOfEmployment,
				provinceOfEmployment:
					item.provinceOfEmployment && item.provinceOfEmployment.id
						? item.provinceOfEmployment.id
						: getProvinceDDLLabelByShortCode(provinceOfEmployementDDL, item.provinceOfEmployment),
				country: item.country && item.country.id ? item.country.id : item.country,
				// province: item.province && item.province.id ? item.province.id : item.province,
				province:
					item.province && item.province.id ? item.province.id : getProvinceDDLLabelByShortCode(provinceOfEmployementDDL, item.province),
				states: item.states && item.states.id ? item.states.id : item.states,
				city: item.city && item.city.id ? item.city.id : item.city,
			}))
		);
	}, [employeeList, provinceOfEmployementDDL]);


	const expandedLabels = ["Employee"];
	const expandedEmployeeListOrSetupItem = "List"; // Special case for "List" under "Employee"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Employee" &&
				item.label === expandedEmployeeListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false, // 🔥 fix: only expand if has sub-items
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedEmployeeListOrSetupItem && "/employee/list",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const requiredFields = [
		"firstName",
		"lastName",
		"dateOfBirth",
		"addressLine1",
		"country",
		"province",
		"states",
		"postCode",
		"zipCode",
		"dateOfHire",
		"provinceOfEmployment",
	];

	// const validateSelectedItems = (items: any[]) => {
	// 	items.forEach((item, index) => {
	// 		const missingFields: string[] = [];
	// 		// Check socialInsuranceNumber if it exists
	// 		const sin = item?.socialInsuranceNumber;

	// 		const itemsWithCountryOrProvinceObjects = items.map((item) => {
	// 			const match = employeeList?.items.find((d:any) => d.id === item.id);
	// 			return {
	// 				...item,
	// 				province: match?.province || undefined,
	// 				country: match?.country || undefined,
	// 				state: match?.state || undefined,
	// 				city: match?.city || undefined,
	// 				provinceOfEmployment: match?.provinceOfEmployment || undefined,
	// 			};
	// 		});

	// 		requiredFields.forEach((field) => {
	// 			const value = item?.[field];

	// 			// For nested object like provinceOfEmployment, check if it's not null
	// 			if (
	// 				field === "provinceOfEmployment"
	// 					? !value
	// 					: value === null || value === ""
	// 			) {
	// 				// Convert camelCase to normal for display: "firstName" => "First Name"
	// 				const fieldLabel = field
	// 					.replace(/([A-Z])/g, " $1")
	// 					.replace(/^./, (str) => str.toUpperCase());

	// 				missingFields.push(fieldLabel.trim());
	// 			}
	// 		});



	// 		if (missingFields.length > 0) {
	// 		
	// 		
	// 			setSnackbarData({
	// 				open: true,
	// 				message: `${missingFields.join(
	// 					" and "
	// 				)} value is missing`,
	// 				severity: "error",
	// 			});
	// 		} else if (sin && sin.trim() !== "") {
	// 			const cleanedSIN = sin.replace(/-/g, "");
	// 			const isValid = checkSINValidity(cleanedSIN);

	// 	
	// 			if (cleanedSIN.length === 9 && isValid % 10 === 0) {
	// 			
	// 				// Submit API call here
	// 			} else {
	// 				console.error(
	// 					`Item ${index + 1}: Invalid Social Insurance Number ❌`
	// 				);
	// 				setSnackbarData({
	// 					open: true,
	// 					message: `Invalid Social Insurance Number ❌`,
	// 					severity: "error",
	// 				});
	// 			}
	// 		} else {
	// 			alert(`ALL OK`);
	// 			// Submit API call here

	// 			const payload = itemsWithCountryOrProvinceObjects.map((item: any) => ({
	// 				// ...item,
	// 				employeeId: item.employeeId,
	// 				clientId: clientIdStore,
	// 				firstName: item.firstName,
	// 				lastName: item.lastName,
	// 				initial: item.initial,
	// 				gender: item.gender,
	// 				dateOfBirth: item.dateOfBirth,
	// 				socialInsuranceNumber: item.socialInsuranceNumber,
	// 				address: {
	// 				countryType: true,
	// 				country: item.country,
	// 				province: item.province,
	// 				states: item.states,
	// 				addressLine1: item.addressLine1,
	// 				city: item.city,
	// 				postCode: item.postCode,
	// 				phone: item.phone,
	// 				email: item.email
	// 				},
	// 				employeeSetub: {
	// 				stubLanguage: item.stubLanguage,
	// 				emailStub: item.emailStub,
	// 				printHourlyRateOnStub: item.printHourlyRateOnStub,
	// 				stubNotes: item.stubNotes
	// 				},
	// 				employeeBank: {
	// 				account1Name: item.account1Name,
	// 				accountNo: item.accountNo,
	// 				branchTransitNumber: item.branchTransitNumber,
	// 				bankFinancialNo: item.bankFinancialNo,
	// 				account2Name: item.account2Name,
	// 				account2No: item.account2No,
	// 				branchTransitNumberForAccount2: item.branchTransitNumberForAccount2,
	// 				bankFinancialNoForAccount2: item.bankFinancialNoForAccount2,
	// 				split: item.split,
	// 				percenagePerDollorInAccount1: item.percenagePerDollorInAccount1
	// 				},
	// 				employeePayroll: {
	// 				payFrequency: item.payFrequency,
	// 				dateOfHire: item.dateOfHire,
	// 				payMethod: item.payMethod,
	// 				provinceOfEmployment: item.provinceOfEmployment,
	// 				includeInT4OrRl1: item.includeInT4OrRl1,
	// 				dentalBenefits: item.dentalBenefits,
	// 				vacationPay: item.vacationPay,
	// 				payOutVacationPay: item.payOutVacationPay,
	// 				vacationPayOpenBalance: item.vacationPayOpenBalance
	// 				},
	// 				additionalInformation: {
	// 				datePensionBecomePayable: item.datePensionBecomePayable,
	// 				rppOrDpspRegistrationNumber: item.rppOrDpspRegistrationNumber,
	// 				dateOfDisability: item.dateOfDisability,
	// 				dateDisabilityWasCeased: item.dateDisabilityWasCeased
	// 				},
	// 				id: item.id
	// 			}))

	// 	
	// 			submitUpdatedRows(payload)

	// 		}
	// 	});
	// };

	const validateSelectedItems = (items: any[]) => {
		const allErrors: string[] = [];
		const validPayload: any[] = [];

		// const itemsWithCountryOrProvinceObjects = items.map((item) => {
		// 	const match = employeeList?.items.find((d: any) => d.id === item.id);
		// 	return {
		// 		...item,
		// 		province: match?.province || undefined,
		// 		country: match?.country || undefined,
		// 		state: match?.state || undefined,
		// 		city: match?.city || undefined,
		// 		provinceOfEmployment: match?.provinceOfEmployment || undefined,
		// 	};
		// });

		const itemsWithCountryOrProvinceObjects = items.map((item) => {
			const regenrateProvinceDDLItems = provinceOfEmployementDDL.map(({ label, value, shortCode }: any) => ({
				name: label,
				id: value,
				shortCode,
			}));
			const selectedProvince = regenrateProvinceDDLItems.find(
				(sc: any) => item.province === sc.shortCode || item.province === sc.id || item.province === sc.name
			);
			const selectedProvinceOfEmployment = regenrateProvinceDDLItems.find(
				(sc: any) => item.provinceOfEmployment === sc.shortCode || item.provinceOfEmployment === sc.id || item.provinceOfEmployment === sc.name
			);
			const match = employeeList?.items.find((d: any) => d.shortCode === item.shortCode);
			return {
				...item,
				province: selectedProvince || undefined,
				country: match?.country || undefined,
				state: match?.state || undefined,
				city: match?.city || undefined,
				provinceOfEmployment: selectedProvinceOfEmployment,
			};
		});

		itemsWithCountryOrProvinceObjects.forEach((item, index) => {
			const missingFields: string[] = [];
			const sin = item?.socialInsuranceNumber;

			requiredFields.forEach((field) => {
				const value = item?.[field];

				if (
					field === "provinceOfEmployment"
						? !value
						: value === null || value === ""
				) {
					const fieldLabel = field
						.replace(/([A-Z])/g, " $1")
						.replace(/^./, (str) => str.toUpperCase());

					missingFields.push(fieldLabel.trim());
				}
			});

			if (missingFields.length > 0) {
				allErrors.push(
					`${missingFields.join(" and ")} value is missing`
				);
				return; // Skip this item
			}

			if (!maskStatus && sin && sin.trim() !== "") {
				const cleanedSIN = sin.replace(/-/g, "");
				const isValid = checkSINValidity(cleanedSIN);

				if (cleanedSIN.length !== 9 || isValid % 10 !== 0) {
					allErrors.push(`Invalid Social Insurance Number`);
					return; // Skip this item
				}
			}

			// If all validations passed, push to payload
			validPayload.push({
				employeeCode: item.employeeCode,
				clientId: clientIdStore,
				firstName: item.firstName,
				lastName: item.lastName,
				initial: item.initial,
				gender: item.gender,
				dateOfBirth: item.dateOfBirth && item.dateOfBirth !== "" && item.dateOfBirth.charAt(2) !== "*" ? formatDate(item.dateOfBirth) : item.dateOfBirth,
				socialInsuranceNumber: item.socialInsuranceNumber,
				address: {
					countryType: true,
					country: item.country,
					province: item.province,
					states: item.states,
					addressLine1: item.addressLine1,
					city: item.city,
					postCode: item.postCode,
					phone: item.phone,
					email: item.email
				},
				employeeSetub: {
					stubLanguage: item.stubLanguage,
					emailStub: item.emailStub,
					printHourlyRateOnStub: item.printHourlyRateOnStub,
					stubNotes: item.stubNotes
				},
				employeeBank: {
					account1Name: item.account1Name,
					accountNo: item.accountNo,
					branchTransitNumber: item.branchTransitNumber,
					bankFinancialNo: item.bankFinancialNo,
					account2Name: item.account2Name,
					account2No: item.account2No,
					branchTransitNumberForAccount2: item.branchTransitNumberForAccount2,
					bankFinancialNoForAccount2: item.bankFinancialNoForAccount2,
					split: item.split,
					percenagePerDollorInAccount1: item.percenagePerDollorInAccount1
				},
				employeePayroll: {
					payFrequency: item.payFrequency,
					payPeriod: item.payPeriod,
					dateOfHire: item.dateOfHire && item.dateOfHire !== "" ? formatDate(item.dateOfHire) : item.dateOfHire,
					// payMethod: item.payMethod,
					payMethod: companyInformation && companyInformation.paymentSetup === 100 ? 200 : item.payMethod,
					provinceOfEmployment: item.provinceOfEmployment,
					includeInT4OrRl1: item.includeInT4OrRl1,
					dentalBenefits: item.dentalBenefits,
					vacationPay: item.vacationPay,
					payOutVacationPay: item.payOutVacationPay,
					vacationPayOpenBalance: item.vacationPayOpenBalance
				},
				additionalInformation: {
					datePensionBecomePayable: item.datePensionBecomePayable,
					rppOrDpspRegistrationNumber: item.rppOrDpspRegistrationNumber,
					dateOfDisability: item.dateOfDisability,
					dateDisabilityWasCeased: item.dateDisabilityWasCeased
				},
				id: item.id
			});
		});

		if (allErrors.length > 0) {
			// Show first error or all errors, up to you
			setSnackbarData({
				open: true,
				message: allErrors.join("\n"),
				severity: "error",
			});
		} else {
			console.log('validPayload', validPayload)
			// console.log('validPayload', JSON.stringify(validPayload))
			// console.log('payFrequencyDDLItems', payFrequencyDDLItems)
			submitUpdatedRows(validPayload);
		}
	};


	const onSubmitButtonClickedHandler = () => {


		// const updatedActualListData =
		// 	employeeList &&
		// 	employeeList?.items.map((item: any) => ({
		// 		...item,
		// 		provinceOfEmployment: item.provinceOfEmployment.id,
		// 	}));
		// const changed =
		// 	employeeListTableData &&
		// 	employeeListTableData.filter(
		// 		(item, i) =>
		// 			JSON.stringify(item) !== JSON.stringify(updatedActualListData[i])
		// 	);

		const updatedSelectedEmployeeListDataItems = employeeListTableData.filter(
			(x: any) => selectedEmployeeListItems.some((y: any) => y.id === x.id)
		);

		if (updatedSelectedEmployeeListDataItems && updatedSelectedEmployeeListDataItems.length > 0) {

			const validatedData = validateSelectedItems(updatedSelectedEmployeeListDataItems);

		} else {
			setSnackbarData({
				open: true,
				message: "Please select atleast one item.",
				severity: "warning",
			});
		}
	};

	// const [provinceOfEmployementDDL, setprovinceOfEmployementDDL] =
	// 	useState<any>(undefined);
	// useEffect(() => {
	// 	if (countryList && Array.isArray(countryList)) {
	// 		const canada = countryList.find(
	// 			(item: any) => item.label === "Canada" || "canada"
	// 		);
	// 		if (canada) {
	// 			(async () => {
	// 				try {
	// 					const response = await CountryService.getProvinceOrStateDDL(
	// 						navigateToLogout,
	// 						canada.value,
	// 						"Province"
	// 					);
	// 			
	// 					setprovinceOfEmployementDDL(response);
	// 				} catch (err) {
	// 					console.error("error", err);
	// 				}
	// 			})();
	// 		} else {
	// 			setprovinceOfEmployementDDL(undefined);
	// 		}
	// 	}
	// }, [countryList]);

	let completeHeaderColumnsList = {
		employeeId: false,
		firstName: true,
		lastName: true,
		initial: false,
		gender: false,
		dateOfBirth: false,
		socialInsuranceNumber: true,
		zipCode: true,
		country: false,
		province: true,
		states: true,
		addressLine1: true,
		city: true,
		postCode: true,
		phone2: false,
		stubEmailFrom: false,
		email: false,
		phone: false,
		stubLanguage: false,
		emailStub: false,
		printHourlyRateOnStub: false,
		stubNotes: false,
		payFrequency: true,
		dateOfHire: true,
		payMethod: true,
		provinceOfEmployment: true,
		includeInT4OrRl1: false,
		dentalBenefits: false,
		vacationPay: false,
		payOutVacationPay: false,
		vacationPayOpenBalance: false,
		datePensionBecomePayable: false,
		rppOrDpspRegistrationNumber: false,
		dateOfDisability: false,
		dateDisabilityWasCeased: false,
		date: false,
		howIsEmployPaid: false,
		regularSalaryForThePeriod: true,
		hourlyRate: true,
		regularHours: true,
		minVocationPayOfGross: true,
		otherFederalTaxCreditsAuthorizedByTaxCenterMedicalExpensesChartiableDonations:
			false,
		otherAnnualProvincialNonRefundableTaxCreditsAuthorizedByTaxCenter: false,
		contributionToTheFondsDeSolidariteDesTravailleursDuQuebecFTQOrToFondaction:
			false,
		numberOfDisabledDependants: false,
		noOfDependantsUnderAge18ForWhichTheEmployeeOrHasMadeAWrittenRequest: false,
		annualDeductionAuthorizedInFormTp1016V: false,
		tp1016VFilledBeforeFirstPeriodInTheYear: false,
		cppQppExempt: false,
		eiExempt: false,
		federalTaxExempt: false,
		provisionalTaxExempt: false,
		qpipExempt: false,
		csstExempt: false,
		wsibWcbExemptAs: false,
		erdEarning: false,
		erdReimbursement: false,
		erdDeduction: false,
		value: true,
		provincialValue: true,
	};

	let itams = [
		{
			id: "1",
			firstName: "Zeeshan",
			lastName: "Ali",
			socialInsuranceNumber: "121-212-120",
			payFrequency: "26",
			dateOfHire: "2025-05-30",
			payMethod: 100,
			provinceOfEmployment: "AB",
		},
		{
			id: "2",
			firstName: "Mudassar",
			lastName: "Hanif",
			socialInsuranceNumber: "000-000-000",
			payFrequency: "26",
			dateOfHire: "2025-05-30",
			payMethod: 100,
			provinceOfEmployment: "BC",
		},
		{
			id: "3",
			firstName: "Bilal",
			lastName: "Khan",
			socialInsuranceNumber: "145-236-789",
			payFrequency: "26",
			dateOfHire: "2025-05-30",
			payMethod: 100,
			provinceOfEmployment: "YK",
		},
	];
	//   ;

	// Row Data: The data to be displayed.
	// const [rowData, setRowData] = useState([
	// 	{ make: "Tesla", model: "Model Y", price: 64950, electric: true },
	// 	{ make: "Ford", model: "F-Series", price: 33850, electric: false },
	// 	{ make: "Toyota", model: "Corolla", price: 29600, electric: false },
	// 	{ make: "Mercedes", model: "EQA", price: 48890, electric: true },
	// 	{ make: "Fiat", model: "500", price: 15774, electric: false },
	// 	{ make: "Nissan", model: "Juke", price: 20675, electric: false },
	// ]);

	// // Column Definitions: Defines & controls grid columns.
	// const [colDefs, setColDefs] = useState([
	// 	{ field: "make"

	// 	 },
	// 	{ field: "model" },
	// 	{ field: "price",
	// 		valueFormatter: (p:any) => p.value.toLocalString()
	// 	 },
	// 	{ field: "electric" },
	// ]);

	// const defaultColDef = {
	// flex: 1,
	// };

	const isEmployeeGridLoading =
		isPendingPayFrequencyDDL || isPendingClientInformationById;


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return employeeList &&
		employeeList.items &&
		employeeList.items.length === 0 &&
		zeroEmployee === true ? (
		<EmptyList
			showImage={true}
			imagePath={image}
			heading={`There's nothing! Add Employee to proceed`}
			content={`You can add Employee by directly clicking on add Employee button.`}
			testId={"employee-empty-list"}
			showAddButton={true}
			addButtonLabel={"Add Employee"}
			navigateToAdd={"/employee/setup"}
			showImportButton={false}
			showImportXmlButton={false}
		/>
	) : (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						// isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isSubmittingColumnReorder ||
						isSubmittingUpdatedRows ||
						isDeleting || isBulkDeleting
					}
					testId="employee-list"
				/>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={
						bulkEmployeeDelete === true
							? deleteBulkEmployeeField
							: deleteEmployeeField
					}
					cancelActionHandler={onCloseHandler}
					testId="tax-profile-tab-confirmation-dialog"
				/>
				{isEmployeeGridLoading ? (
					<SkeletonLoadingDataGrid pageTitle="Employee" />
				) : (<PrimeDataGridWithColumnToggled
					columns={
						employeeList
							? employeeList.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
					}
					// completeHeaderColumnsList={completeHeaderColumnsList}
					completeHeaderColumnsList={
						employeeList &&
							Object.keys(employeeList.totalHeaderColumns).length !== 0
							? employeeList.totalHeaderColumns
							: {}
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						employeeList && upadtedEmployeeListItems
							? upadtedEmployeeListItems
							: []
						// itams
					}
					showIndexColumn={false}
					showChecboxColumn={true}
					showSettingColumn={true}
					onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					isLoading={isPending}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						employeeList && employeeList.total ? employeeList.total : 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					searchFieldPlaceholder={"Search..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Employee"}
					provinceOfEmployementDDL={provinceOfEmployementDDL}
					countryList={countryList ?? []}
					employeeListTableData={(data: any) => setEmployeeListTableData(data)}
					payFrequencyDDLItems={payFrequencyDDLItems && payFrequencyDDLItems}
					companyInformation={companyInformation}
					maskStatus={maskStatus}
				/>)}

				{/* <AgDataGridReact rows={rowData} columns={colDefs}/> */}
				{/* toolbar */}
				{/* {employeeList && provinceOfEmployementDDL ? ( */}
				{!isPending && !isPendingPayFrequencyDDL && !isPendingClientInformationById ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							// backgroundColor: "#fff",
							backgroundColor: "#F7F7F7",
							// marginTop: theme.spacing(3),
							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					>
						<Toolbar>
							<EnhancedButtonPrimeReact
								label="Submit"
								// type="submit"
								onClickHandler={onSubmitButtonClickedHandler}
								// icon="pi pi-check"
								raised
								severity={EnhancedButtonPrimeReactSeverity.Success}
							/>
						</Toolbar>
					</AppBar>
				) : (
					<></>
				)}
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};


{/* <div>
			<div style={{ width: "50%" }}>
				<div style={{ padding: "10px", marginTop: "10px", marginBottom: "10px" }}>
					<p>Dear Zeeshan ,</p>
					<p style={{ marginBottom: 0 }}>Thank you for choosing SimplePay. Your account has been successfully created.</p>
					<p style={{ marginTop: 0 }}>To proceed please click on the button to verify your account.</p>

					<div>
						<a href="{subject_link}">
							<button style={{
								padding: "5px",
								backgroundColor: "#647488", color: "#fff", borderRadius: "2px", border: "5px solid #647488", cursor: "pointer"
							}}>Verify Account</button>
						</a>
					</div>
					<p style={{ marginBottom: 0 }}>Please refer to following guide to get started:</p>
					<a href="#" target="_blank" style={{ marginTop: 0, cursor: "pointer", color: "rgb(0, 145, 179)", textDecoration: "none" }}>1st Login Guide</a>
					<p>Thanks again for choosing SimplePay. We value your business.</p>
					<p style={{ marginBottom: 0 }}>SimplePay Team</p>
					<a href="https://v3.simplepay.ca/" target="_blank" style={{ marginTop: 0, cursor: "pointer", color: "rgb(0, 145, 179)", textDecoration: "none" }}>www.simplepay.ca</a>
					<div style={{ marginTop: "1rem" }}>
						<p style={{ margin: 0 }}>Technology Element Inc.</p>
						<p style={{ margin: 0 }}>1550 South Gateway Rd</p>
						<p style={{ margin: 0 }}>Suite 229</p>
						<p style={{ margin: 0 }}>Mississauga, ON</p>
						<p style={{ margin: 0 }}>L4W 5G6</p>
						<p style={{ margin: 0 }}>P 416.363.2226</p>
						<p style={{ margin: 0 }}>F 905.624.8722</p>
					</div>
				</div>
			</div>
		</div> */}